import { create } from "zustand";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_URL = process.env.REACT_APP_API_URL;

export const useAuthStore = create((set, get) => ({
    token: null,
    companyId: null,

    user: null,
    companyDetails: null,
    integrationToken: null,
    users: null,
    activeUsers: null,

    loading: false,
    companyLoading: false,
    usersLoading: false,
    activeUsersLoading: false,

    // ================= SSO =================
    setSSOToken: async (ssoToken) => {
        try {
            set({ loading: true });

            const decoded = jwtDecode(ssoToken);
            const companyId = decoded?.company_id;

            set({
                token: ssoToken,
                companyId,
                user: decoded,
            });

            await get().getCompanyDetails();

            set({ loading: false });
        } catch (err) {
            console.error(" SSO ERROR:", err);
            set({ loading: false });
        }
    },

    // ================= COMPANY =================
    getCompanyDetails: async () => {
        try {
            const { token, companyId } = get();


            if (!token || !companyId) return;

            set({ companyLoading: true });

            const res = await axios.get(`${API_URL}/settings/company/${companyId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            set({
                companyDetails: res.data,
                companyLoading: false,
            });

            await get().getIntegrationToken();
        } catch (err) {
            set({ companyLoading: false });
        }
    },

    // ================= INTEGRATION TOKEN =================
    getIntegrationToken: async () => {
        try {
            const { companyDetails } = get();

            if (!companyDetails) return;

            const baseUrl = companyDetails.url.startsWith("http")
                ? companyDetails.url
                : `https://${companyDetails.url}`;

            const params = new URLSearchParams();
            params.append("username", companyDetails.username);
            params.append("password", companyDetails.password);

            const res = await axios.post(`${baseUrl}/api/generate_token`, params, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });

            const token =
                res.data?.token ||
                res.data?.data?.token ||
                res.data?.access_token ||
                res.data?.result?.token;

            if (!token) {
                return;
            }

            set({ integrationToken: token });

            await get().getUsers(token);
        } catch (err) { }
    },

    getUsers: async (tokenFromArg) => {
        try {
            const { companyDetails } = get();

            const integrationToken = tokenFromArg || get().integrationToken;

            if (!integrationToken || !companyDetails) return;

            set({ usersLoading: true });

            const baseUrl = companyDetails.url.startsWith("http")
                ? companyDetails.url
                : `https://${companyDetails.url}`;

            const res = await axios.get(`${baseUrl}/api/get_users`, {
                headers: {
                    Authorization: `Bearer ${integrationToken}`,
                },
            });



            const users = res.data?.users || [];

            set({
                users: Array.isArray(users) ? users : [],
                usersLoading: false,
            });

            return users;
        } catch (err) {
            console.error(err);
            set({ usersLoading: false, users: [] });
        }
    },

    getActiveUsers: async (tokenFromArg) => {
        try {
            const { companyDetails } = get();
            const integrationToken = tokenFromArg || get().integrationToken;
            if (!integrationToken || !companyDetails) return;

            set({ activeUsersLoading: true });

            const baseUrl = companyDetails.url.startsWith("http")
                ? companyDetails.url
                : `https://${companyDetails.url}`;

            const res = await axios.get(`${baseUrl}/api/get_users`, {
                headers: { Authorization: `Bearer ${integrationToken}` },
                params: { is_active: 1 },
            });

            const activeUsers = res.data?.users || [];

            set({
                activeUsers: Array.isArray(activeUsers) ? activeUsers : [],
                activeUsersLoading: false,
            });

            return activeUsers;
        } catch (err) {
            console.error(err);
            set({ activeUsersLoading: false, activeUsers: [] });
        }
    },

    // ================= LOGOUT =================
    logout: () => {
        set({
            token: null,
            companyId: null,
            user: null,
            companyDetails: null,
            integrationToken: null,
            users: null,
            activeUsers: null,
        });
    },
}));
